import { entity, legalPaths } from '../entity';
import {
  P, H3, Ul, Callout, Tok,
} from '../blocks';
import type { LegalDoc } from '../types';

// Community Guidelines (English).
//
// These get their legal force from section 6 of the Terms of Service, which
// incorporates them by reference. That is deliberate: keeping them a separate
// document means a new abuse pattern can be added here without amending a
// contract. Do not move this content into the Terms.
//
// The "enforcement" section describes what the code actually does, and it must
// stay true to it:
//   - Comprehend toxicity: >= TOXIC_REJECT_THRESHOLD (0.7) blocks the mutation
//     outright; >= TOXIC_FLAG_THRESHOLD (0.5) sets contentSafe = false, which is
//     a -500 search-score penalty (demotion, NOT removal). See
//     src/apollo/utils/moderateText.ts and src/utils/constants.ts in tribys_back.
//   - Covered fields: profile name and bio, triby title and description, spot
//     name/description/address/eventLocation, new interest names.
//   - Chat messages are NOT scanned. Do not imply otherwise anywhere in here.
//   - Avatars: Rekognition NSFW labels, no-face-detected, and face mismatch
//     against the verification reference image.
// analyzeTexts() is called with LanguageCode 'en' for every request, so
// non-English toxicity detection is materially weaker. Hence the honest hedge in
// "enforcement" rather than a claim that automated checks apply everywhere.
//
// Never claim proactive known-CSAM hash matching: checkKnownCsam is a stub that
// always returns no-match. The reporting commitment below is about what we do on
// actual knowledge, which is a legal obligation under 18 U.S.C. 2258A.

export const guidelinesEn: LegalDoc = {
  lang: 'en',
  title: 'Community Guidelines',
  updatedLabel: `Last updated ${entity.effectiveDate}`,
  contentsLabel: 'Contents',
  altHref: legalPaths.guidelinesEs,
  altLabel: 'Ver en español',
  sections: [
    {
      id: 'what-this-is',
      heading: '1. The point of all this',
      body: (
        <>
          <P>
            Tribys exists so people with shared interests can find each other and
            actually meet up. That only works if the app feels safe enough to
            walk into a room full of strangers. These Guidelines are how we keep
            it that way.
          </P>
          <Callout>
            <strong>
              The short version: be a real person, be decent to people, and
              remember that everyone here may end up in the same room as you.
            </strong>
          </Callout>
          <P>
            These Guidelines are part of our{' '}
            <a href={legalPaths.terms}>Terms of Service</a>, so breaking them
            breaks that agreement. They apply to everything you put into Tribys —
            your profile, your photos, your messages, the tribys you create, the
            Spots you add, and how you behave when you meet people you found
            here.
          </P>
          <P>
            We update these Guidelines as new problems appear. They are not an
            exhaustive list of every bad thing a person could do; if something is
            obviously against the spirit of them, we will act on it anyway.
          </P>
        </>
      ),
    },
    {
      id: 'real-person',
      heading: '2. Be yourself, and only yourself',
      body: (
        <>
          <P>
            Everything here depends on people being who they say they are.
          </P>
          <Ul
            items={[
              'Use your real name and a recent photo of your own face.',
              'One account per person. No second accounts, no accounts for your friend, no accounts to get around a block or a ban.',
              'You must be 18 or older. No exceptions.',
              'Do not pretend to be someone else — another member, a celebrity, a business, an organiser, a public official, or us.',
              'Do not use a profile to represent a business, brand, band, campaign or organisation. Profiles are for people.',
              'Do not put contact details, links, prices or promotional text in your profile in order to move people off Tribys.',
            ]}
          />
          <P>
            Identity verification is optional, and skipping it is fine. But if you
            verify and then upload a photo of someone else, we will notice, and we
            treat it as a serious breach — the whole point of the badge is that it
            cannot be borrowed.
          </P>
        </>
      ),
    },
    {
      id: 'photos',
      heading: '3. Photos',
      body: (
        <>
          <P>Your profile photo should show your face, clearly, and be recent.</P>
          <P>Do not upload:</P>
          <Ul
            items={[
              'photos of anyone who has not agreed to appear on your profile;',
              'photos of children, including your own, as your profile photo;',
              'nudity or sexual content — see section 5;',
              'photos that are mostly text, memes, logos, QR codes or advertisements;',
              'photos you did not take and have no right to use;',
              'graphic violence, injury, or images of a real person’s death;',
              'photos of weapons as the subject of the picture.',
            ]}
          />
          <P>
            Group shots are fine if it is clear which person you are and everyone
            in the photo is comfortable being there.
          </P>
        </>
      ),
    },
    {
      id: 'respect',
      heading: '4. Harassment and hate',
      body: (
        <>
          <P>
            Disagreeing with someone is fine. Making Tribys unpleasant for them is
            not.
          </P>
          <P><strong>Not allowed:</strong></P>
          <Ul
            items={[
              'insults, slurs, name-calling, mockery, or persistent unwanted contact;',
              'messaging someone again after they have made clear they are not interested, or after they have blocked you and you have found another way to reach them;',
              'sexual comments, propositions or innuendo that the other person has not invited;',
              'coordinating with others to pile onto someone, in the app or elsewhere;',
              'attacking, demeaning or dehumanising people because of their race, ethnicity, national origin, immigration status, religion, disability, medical condition, age, sex, gender identity, gender expression, sexual orientation, or any similar characteristic;',
              'hateful symbols, or support for organisations that promote hate or violence;',
              'deliberately misgendering someone, or repeatedly using a name they have asked you not to use;',
              'excluding people from a triby on the basis of any characteristic listed above. The one exception is the gender-composition setting built into creating a triby — a women-only book club or a men’s mental-health group is fine. What is not fine is using a triby’s title or description to keep out, demean or target any group of people.',
            ]}
          />
          <P>
            If a conversation is going badly, you can leave it. Blocking someone
            stops them contacting you or seeing you, and you never have to
            explain why.
          </P>
        </>
      ),
    },
    {
      id: 'sexual',
      heading: '5. Sexual content and nudity',
      body: (
        <>
          <P>
            <strong>Tribys is not a dating app and it is not an adult app.</strong>{' '}
            Keep it to what you would be comfortable with on a first meeting in a
            café.
          </P>
          <P><strong>Not allowed anywhere:</strong></P>
          <Ul
            items={[
              'nudity, partial nudity, or sexually explicit images, in profiles, tribys, Spots or messages;',
              'sexual descriptions or requests sent to someone who has not invited them;',
              'sexual services, escorting, or anything transactional involving sex;',
              'tribys or Spots organised around sexual activity;',
              'links to adult content or adult sites.',
            ]}
          />
          <P>
            Sending an unsolicited sexual image is grounds for immediate removal
            from Tribys, not a warning.
          </P>
        </>
      ),
    },
    {
      id: 'minors',
      heading: '6. Minors — zero tolerance',
      body: (
        <>
          <Callout>
            <strong>
              There is nothing to discuss here. Tribys is 18+. Any sexual content
              involving a minor, and any attempt to contact, groom or arrange to
              meet a minor, results in immediate permanent removal and a report to
              the authorities.
            </strong>
          </Callout>
          <P>
            We remove any account we find belonging to someone under 18. If we
            become aware of content that appears to sexually exploit a child, we
            remove it, we permanently terminate the account, we preserve what the
            law requires us to preserve, and we report it to the National Center
            for Missing &amp; Exploited Children as United States law requires.
          </P>
          <P>
            If you encounter any of this, report it in the app immediately and, if
            you can, email <Tok value={entity.supportEmail} /> so it reaches us
            faster. If a child is in immediate danger, contact your local
            emergency services first.
          </P>
        </>
      ),
    },
    {
      id: 'violence',
      heading: '7. Violence, threats and self-harm',
      body: (
        <>
          <P><strong>Not allowed:</strong></P>
          <Ul
            items={[
              'threatening anyone with violence, or wishing violence on them;',
              'encouraging or organising violence, or praising a violent attack or its perpetrator;',
              'support for or promotion of terrorism or violent extremism;',
              'encouraging anyone to hurt themselves, or promoting suicide, self-harm or eating disorders;',
              'graphic violence or gore;',
              'trafficking in people, or exploiting them.',
            ]}
          />
          <P>
            <strong>If you are struggling</strong>, you are not in breach of
            anything by talking about it. Discussing your own mental health, or
            supporting someone else, is welcome here. What we remove is content
            that encourages harm. If someone is in immediate danger, please
            contact your local emergency or crisis services — we are not a crisis
            service and we cannot respond quickly enough to be one.
          </P>
        </>
      ),
    },
    {
      id: 'illegal',
      heading: '8. Illegal and regulated things',
      body: (
        <>
          <P>
            Do not use Tribys to do, arrange, promote or advertise anything
            unlawful. That includes:
          </P>
          <Ul
            items={[
              'selling or arranging drugs, prescription medication, weapons, ammunition, explosives, or stolen goods;',
              'gambling, lotteries or betting;',
              'counterfeit or unlicensed goods;',
              'fraud, identity theft, or trafficking in accounts or personal data;',
              'organising a gathering that requires a permit you do not have, or that is unlawful where it is happening.',
            ]}
          />
          <P>
            Legal adult activities are fine. A triby that meets at a bar, a
            brewery, a wine tasting or a cigar lounge is welcome; a triby about
            getting hold of drugs is not.
          </P>
        </>
      ),
    },
    {
      id: 'spam',
      heading: '9. Spam, scams and selling',
      body: (
        <>
          <P>
            Tribys is not a marketing channel. It is not a lead list either.
          </P>
          <P><strong>Not allowed:</strong></P>
          <Ul
            items={[
              'sending the same message to lots of people, or messaging people you have no intention of meeting;',
              'promoting a business, product, service, course, coaching, investment or fundraiser;',
              'multi-level marketing, referral farming, crypto or investment schemes, or anything with a downline;',
              'asking members for money, gifts, cards or crypto, for any reason;',
              'phishing, fake links, or pretending to be Tribys support;',
              'using bots, scripts or automation to create accounts, join tribys, or send messages;',
              'collecting members’ profiles, photos, contact details or locations in bulk, for any purpose;',
              'creating tribys or Spots that exist to drive traffic somewhere else.',
            ]}
          />
          <Callout>
            <strong>
              Nobody legitimate will ask you for money on Tribys.
            </strong>{' '}
            If someone moves you off the app quickly, tells a story that needs
            urgent funds, or offers you an investment, it is a scam. Do not send
            anything, and report them — that is what actually gets them removed.
          </Callout>
          <P>
            Organising a meet-up for a hobby you happen to sell things for is a
            grey area. Use judgement: if the tribys is really about the interest,
            fine. If it is really about the sale, it is not.
          </P>
        </>
      ),
    },
    {
      id: 'privacy',
      heading: '10. Other people’s privacy',
      body: (
        <>
          <P>
            People share things here on the understanding that it stays in the
            room.
          </P>
          <Ul
            items={[
              'Do not post anyone’s address, phone number, email, workplace, financial details or documents.',
              'Do not share screenshots of conversations, profiles or member lists outside Tribys.',
              'Do not photograph or record people at a meet-up without asking them.',
              'Do not out anyone — their sexuality, gender identity, health, immigration status, religion, or anything else they have not chosen to share.',
              'Do not try to work out or share where someone lives from what they have posted.',
              'Do not pass on someone’s information to a third party, or use anything you learn here to contact, track or pressure them elsewhere.',
            ]}
          />
          <P>
            Reporting someone to us is not a privacy breach — it is how this
            works, and we treat reports confidentially.
          </P>
        </>
      ),
    },
    {
      id: 'tribys-spots',
      heading: '11. Tribys and Spots',
      body: (
        <>
          <P>
            A triby is a group built around an interest. A Spot is a place or an
            event on the map. Both are created by members, and both are visible
            to people deciding whether to turn up somewhere — so accuracy matters
            more than it might seem.
          </P>
          <H3>Tribys</H3>
          <Ul
            items={[
              'Describe honestly what the group is and what happens at it.',
              'Do not create tribys designed to exclude people on the basis of a characteristic listed in section 4. Choosing a gender composition with the built-in setting is fine; writing exclusions into the title or description is not.',
              'Do not use a triby as an advertisement, a mailing list or a funnel.',
              'If you organise a meet-up, turn up, or tell people you cannot.',
            ]}
          />
          <H3>Spots</H3>
          <Ul
            items={[
              'Add real places and real events, with the right name, address and times.',
              'Do not invent a Spot, add a joke Spot, or put a Spot at a private home.',
              <><strong>Only upload photos you took yourself.</strong> Photos from a business&apos;s website, from social media, from Google or from a review site belong to whoever took them. Logos, posters, flyers and menus belong to whoever designed them. Section 8 of the <a href={legalPaths.terms}>Terms</a> covers this, and you are responsible for it.</>,
              'Do not present a Spot as official, sponsored or endorsed by a business or organiser unless it genuinely is.',
              'Do not bulk-import Spots from another map or directory.',
            ]}
          />
          <P>
            <strong>If a Spot is about your business, venue or event</strong> and
            you want it corrected or taken down, report it using the{' '}
            <strong>Intellectual property</strong> reason or email{' '}
            <Tok value={entity.copyrightEmail} />. Section 9 of the{' '}
            <a href={legalPaths.terms}>Terms</a> sets out the formal copyright
            process.
          </P>
        </>
      ),
    },
    {
      id: 'meeting',
      heading: '12. When you actually meet',
      body: (
        <>
          <P>
            This is the part of Tribys that happens where we cannot see it, and
            the part that matters most. The{' '}
            <a href={legalPaths.terms}>Terms of Service</a> are clear that we do
            not vet anyone and are not part of your plans. So:
          </P>
          <Ul
            items={[
              'Meet in public, at least the first time.',
              'Tell a friend where you are going and who with.',
              'Arrange your own transport, and keep enough charge on your phone to leave.',
              'Do not send money to anyone, before or after meeting them.',
              'Watch your drink, and drink at your own pace.',
              'Leave whenever you want. You do not owe anyone your evening.',
              'Treat people at a meet-up the way these Guidelines require online — harassment in person is a breach just the same, and we act on it.',
              'Report what happened afterwards, even if you handled it. It is how we find repeat offenders.',
            ]}
          />
          <P>
            In an emergency, contact your local emergency services first, then
            tell us.
          </P>
        </>
      ),
    },
    {
      id: 'enforcement',
      heading: '13. How we enforce this',
      body: (
        <>
          <P>
            Some of this is automated and some of it is people. We would rather
            tell you exactly which than let you assume.
          </P>
          <H3>Automated checks, and what they cover</H3>
          <Ul
            items={[
              <><strong>Text you submit is screened for toxicity.</strong> This covers your name and bio, triby titles and descriptions, Spot names, descriptions, addresses and event locations, and new interest names. Above a high confidence threshold the submission is <strong>rejected outright</strong> and you will see an error. In a middle band, the triby or Spot is still created but <strong>ranks lower in search and on the map</strong>.</>,
              <><strong>Profile photos are screened</strong> for nudity and explicit content, for whether a face is actually present, and — if you are verified — for whether the face matches your verification reference image. A photo can be rejected on any of those grounds.</>,
              <><strong>Private messages are not screened.</strong> We do not scan the content of your chats, automatically or otherwise. That means nothing is filtering a message before it reaches you, and it means <strong>reporting is the only way we learn about something said in a chat</strong>. Please use it.</>,
            ]}
          />
          <P>
            Automated checks are not accurate and not complete. They miss things,
            they are weaker in languages other than English, and they sometimes
            reject something perfectly fine. Neither a passed check nor a
            published post means we have approved anything.
          </P>
          <H3>People</H3>
          <P>
            Reports go to our team, who look at the account, the content and the
            history around it, and decide. Automated screening never bans anyone
            on its own — a person decides that.
          </P>
        </>
      ),
    },
    {
      id: 'reporting',
      heading: '14. Reporting and blocking',
      body: (
        <>
          <P>
            <strong>Blocking</strong> is immediate and private, and you do not
            need a reason. A blocked member cannot contact you or see you, and
            they are not told. Use it freely.
          </P>
          <P>
            <strong>Reporting</strong> is how we find out. You can report a
            member, a triby or a Spot from the app, and choose a reason: spam,
            harassment, hate speech, violence, sexual content, self-harm, illegal
            goods, scam, intellectual property, or other. Add a short description
            if you can — what happened and where — because it makes the difference
            between a report we can act on and one we cannot.
          </P>
          <P>
            Reports are confidential. We do not tell the person who reported them.
            We review every report, we may ask you for more detail, and we will
            not always be able to tell you the outcome — but blocking someone
            always works regardless of what we decide about a report.
          </P>
          <P>
            Do not file reports you know to be false, or use reporting to harass
            someone. That is itself a breach.
          </P>
          <P>
            For anything urgent, or anything involving a minor or an immediate
            risk to someone, email <Tok value={entity.supportEmail} /> as well as
            reporting in the app.
          </P>
        </>
      ),
    },
    {
      id: 'consequences',
      heading: '15. What happens when someone breaks these rules',
      body: (
        <>
          <P>
            What we do depends on what happened, whether it looks deliberate, and
            whether it has happened before. Our options are:
          </P>
          <Ul
            items={[
              <><strong>A rejected submission.</strong> The text or photo is not accepted and you can change it and try again.</>,
              <><strong>Reduced visibility.</strong> A triby or Spot stays up but ranks lower.</>,
              <><strong>Content removal.</strong> We remove a photo, a triby, a Spot or a message.</>,
              <><strong>A warning.</strong> We tell you what the problem is and expect it to stop.</>,
              <><strong>Loss of a feature.</strong> For example the verified badge, or the ability to create tribys.</>,
              <><strong>A temporary suspension.</strong> Your account is unusable for a set period, then comes back.</>,
              <><strong>A permanent ban.</strong> Your account is closed and you may not create another.</>,
            ]}
          />
          <P>
            We do not always start at the top of that list.{' '}
            <strong>
              Anything involving a minor, a credible threat, sexual content sent
              to someone who did not ask for it, or a scam goes straight to a
              permanent ban.
            </strong>{' '}
            Creating a new account to get around a ban is itself grounds for
            banning the new one.
          </P>
          <P>
            If you are banned, you keep the right to ask us for your data and to
            have it deleted — see the{' '}
            <a href={legalPaths.privacy}>Privacy Policy</a>. A ban is not a
            refund of anything you paid; section 11 of the{' '}
            <a href={legalPaths.terms}>Terms</a> covers that.
          </P>
        </>
      ),
    },
    {
      id: 'appeals',
      heading: '16. If you think we got it wrong',
      body: (
        <>
          <P>
            We will sometimes be wrong. Automated checks reject things they should
            not, and people misread situations.
          </P>
          <P>
            <strong>
              Email <Tok value={entity.supportEmail} /> with &quot;Appeal&quot; in
              the subject line
            </strong>{' '}
            and tell us the account, what was removed or restricted, and why you
            think the decision was wrong. Anything you want us to take into
            account, put in that email.
          </P>
          <P>
            <strong>A person will review it.</strong> If a decision about you was
            made or assisted by automated processing — a rejected photo, a
            rejected description, a face that did not match — you can ask for a
            human to look at it, ask us to explain the main reasons for it, and
            give us your point of view. We record the outcome, and if we were
            wrong we reverse it and say so.
          </P>
          <P>
            Residents of Quebec and Canada: this is the review right described in
            our <a href={legalPaths.privacy}>Privacy Policy</a>, and you may also
            contact the privacy authority named there.
          </P>
        </>
      ),
    },
    {
      id: 'contact',
      heading: '17. Contact',
      body: (
        <>
          <Ul
            items={[
              <>Reports, appeals and anything urgent: <Tok value={entity.supportEmail} /></>,
              <>Copyright, trademark and Spot rights complaints: <Tok value={entity.copyrightEmail} /></>,
              <>Privacy and data requests: <Tok value={entity.privacyEmail} /></>,
            ]}
          />
          <P>
            <Tok value={entity.legalName} />
            <br />
            <Tok value={entity.address} />
          </P>
        </>
      ),
    },
  ],
};
